import { DataGrid, EditorUtils, notifySuccess, tryGetWidget, Widget } from "@serenity-is/corelib";
import { installFilterPills } from "./FilterPills";
import { exportCsv, printGrid } from "./GridExport";
import { recordCount } from "./GridRowFlags";

/**
 * Rearranges every Serenity list page into the list screen both design handoffs draw.
 *
 * WHAT THE DESIGN ASKS FOR THAT SERENITY DOES NOT SHIP
 * The docs draw a list page as: a page header (title, record-count pill, actions) ABOVE
 * the card; then inside the card a tools row (search, a "Filters" toggle carrying a
 * count, removable chips for what is currently filtered, and a Compact/Comfortable
 * density switch); then a collapsible filter panel; then the table; then the pager.
 *
 * Serenity ships the *capabilities* — quick search, quick filters, a column picker, a
 * pager — but arranges them as one flat toolbar strip inside the card, with the filter
 * editors always visible, no indication of how many are active, no way to clear one
 * without finding its editor, and no density control at all. So this module does three
 * things: it MOVES the existing controls into the designed slots (they keep their own
 * handlers and wiring, so nothing is reimplemented), and it ADDS the two genuinely
 * missing controls — the filter chips and the density switch.
 *
 * WHY IT HOOKS DataGrid.onAfterInit RATHER THAN EACH GRID
 * That static PubSub fires once per grid, for every grid in the application. Subscribing
 * to it here means no list page has to know this module exists, no *Grid.ts needs a base
 * class swapped, and any module added later is styled the moment it is created. The
 * alternative — editing UserGrid, RoleGrid, LanguageGrid, TranslationGrid and every
 * future one — would need remembering forever, which is exactly how a design system
 * rots.
 *
 * Everything here is presentation and arrangement. No request, no column, no permission
 * check and no editor is touched, so a grid behaves identically with this module removed.
 */

const ENHANCED = "m-list-chrome";
const DENSITY_KEY = "BWDesignSystem.GridDensity";

type Density = "compact" | "comfortable";

/** Row heights per version, from the handoffs: v1 is the dense generation, v2 the roomy one. */
function rowHeights(): Record<Density, number> {
    const v2 = document.documentElement?.getAttribute("data-theme-version") === "v2";
    return v2 ? { compact: 44, comfortable: 58 } : { compact: 32, comfortable: 44 };
}

function readDensity(): Density {
    try {
        return localStorage.getItem(DENSITY_KEY) === "comfortable" ? "comfortable" : "compact";
    } catch {
        // Private browsing / storage disabled — the control still works, it just does
        // not survive a reload. Not worth failing the whole page over.
        return "compact";
    }
}

function writeDensity(value: Density) {
    try { localStorage.setItem(DENSITY_KEY, value); } catch { /* see readDensity */ }
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls?: string, text?: string) {
    const node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
}

export function installListPageChrome() {
    DataGrid.onAfterInit.subscribe(e => {
        // One bad grid must not take the page down with it — a list that renders in
        // stock Serenity chrome is far better than a list that does not render.
        try { enhance(e.dataGrid); }
        catch (err) { console.error("[BWDesignSystem] list chrome failed for a grid", err); }
    });
}

function enhance(grid: DataGrid<any>) {
    const card = grid?.domNode;
    if (!card || card.classList.contains(ENHANCED)) return;
    card.classList.add(ENHANCED);

    const head = el("div", "m-list-head");
    const heading = el("div", "m-list-heading");
    const actions = el("div", "m-list-actions");
    head.append(heading, actions);
    card.parentElement?.insertBefore(head, card);

    // The title keeps its own node so anything Serenity does to it later still works.
    const title = card.querySelector<HTMLElement>(":scope > .grid-title");
    if (title) heading.appendChild(title);

    const count = el("span", "m-list-count");
    heading.appendChild(count);

    const tools = el("div", "m-grid-tools");
    card.insertBefore(tools, card.firstChild);

    const quickSearch = card.querySelector<HTMLElement>(".s-QuickSearchBar");
    if (quickSearch) tools.appendChild(quickSearch);

    const filtersBar = card.querySelector<HTMLElement>(".quick-filters-bar");
    let panel: HTMLElement | null = null;
    let filterBtn: HTMLButtonElement | null = null;
    const chips = el("div", "m-chips");

    // ⚠ The two generations diverge here, and this is the one structural branch in this
    // module. v1's prototype replaces the panel with a row of filter PILLS, each opening its
    // own popover; v2's handoff keeps the Filters toggle and the collapsible panel. Read at
    // construction, which is safe because the version switcher reloads.
    const isV1 = document.documentElement?.getAttribute("data-theme-version") === "v1";

    if (filtersBar && isV1) {
        installFilterPills(card, tools, filtersBar, grid);
    }
    else if (filtersBar) {
        panel = el("div", "m-filter-panel");
        panel.hidden = true;
        card.insertBefore(panel, tools.nextSibling);

        panel.appendChild(filtersBar);

        filterBtn = el("button", "m-filter-toggle") as HTMLButtonElement;
        filterBtn.type = "button";
        filterBtn.append(el("span", undefined, "Filters"), el("span", "m-filter-count"));
        filterBtn.addEventListener("click", () => {
            panel!.hidden = !panel!.hidden;
            filterBtn!.classList.toggle("is-open", !panel!.hidden);
            // The grid sizes its canvas to the space it has; opening a panel above it
            // changes that space, and SleekGrid only recomputes when told to.
            grid.slickGrid?.resizeCanvas?.();
        });
        tools.append(filterBtn, chips);
    }

    tools.appendChild(el("div", "m-grid-tools-spacer"));

    // Everything Serenity put in the toolbar that is an action (New, Refresh, Columns)
    // belongs in the page header per the design; the toolbar itself is left in place but
    // collapses to nothing once emptied.
    const toolbar = card.querySelector<HTMLElement>(":scope > .s-Toolbar");
    if (toolbar) {
        toolbar.querySelectorAll<HTMLElement>(".tool-button").forEach(b => actions.appendChild(b));

        // Serenity's toggles (currently only "include deleted", drawn for any grid whose row
        // is soft-deletable) are grid STATE, not page actions, so they belong in the tools row
        // beside the density switch rather than up in the header with New and Refresh.
        //
        // ⚠ They are <div class="s-ToggleButton"> holding an EMPTY <a> whose only content is a
        // title attribute. That fails twice here: it is not a .tool-button so it was left
        // behind in the toolbar, and the toolbar was then judged empty and hidden — so the
        // control existed, answered getBoundingClientRect with 0x0, and could not be reached
        // at all. It also needs a real label; a tooltip-only control is the same defect as the
        // validation message Serenity collapses to a bare red dot.
        toolbar.querySelectorAll<HTMLElement>(".s-ToggleButton").forEach(t => {
            const a = t.querySelector("a");
            if (a && !a.textContent?.trim()) {
                // Serenity's own text is lowercase ("display inactive records") and both
                // handoffs use sentence case. Lifting the first character here rather than
                // hardcoding an English label keeps the string localised — and it has to be
                // JS, not CSS: ::first-letter does not apply to a flex container, whose text
                // is an anonymous flex item.
                const label = a.getAttribute("title") || "";
                a.textContent = label ? label.charAt(0).toLocaleUpperCase() + label.slice(1) : "Show deleted";
            }
            t.classList.add("m-grid-toggle");
            tools.appendChild(t);
        });

        // ⚠ Test for real content, not for child elements. Moving the buttons out leaves
        // the empty .tool-group wrappers behind, so "has any child" is always true and
        // the emptied toolbar kept occupying a 35px band under the tools row.
        const stillUseful = toolbar.querySelector("input, select, button, .tool-button, .editor, .widget-wrapper, .s-ToggleButton");
        if (!stillUseful && !toolbar.textContent?.trim()) toolbar.classList.add("m-empty-toolbar");
    }

    buildHeaderActions(actions, grid);

    tools.appendChild(buildDensity(grid));

    const refreshChips = () => syncFilters(card, chips, filterBtn, grid);
    refreshChips();

    // Quick filter editors are ordinary widgets, so a change on any of them bubbles to
    // the panel; no per-editor subscription is needed.
    panel?.addEventListener("change", () => setTimeout(refreshChips, 0), true);

    // ⚠ The pill reports the grid's TOTAL record count, not the rows on screen — see
    // recordCount. Two earlier readings were both wrong: the raw view length said
    // "26 records" for 20 franchisees with one row expanded (spacers counted), and
    // counting data rows then said "20 records" for 21 franchisees whenever the page size
    // was smaller than the result set, contradicting the pager right below it.
    const refreshCount = () => {
        const total = recordCount(grid.view);
        count.textContent = `${total} ${total === 1 ? "record" : "records"}`;
    };
    refreshCount();
    grid.onDataChanged?.subscribe(() => { refreshCount(); refreshChips(); });
    // ⚠ onDataChanged does NOT fire on a page or page-size change — the items change but
    // the view raises paging info separately — so without this the pill kept the previous
    // page's number. It is the same subscription the pager itself uses.
    grid.view?.onPagingInfoChanged?.subscribe?.(() => refreshCount());

    applyDensity(grid, readDensity());
}

/**
 * The prototype's header action set: **Columns · Export · Print · Refresh**, then a hairline,
 * then the primary New button (eFMS Prototype.dc.html, FRANCHISEES GRID).
 *
 * Two of the four already exist — Serenity ships a column picker and a refresh, just as
 * ICON-ONLY buttons with the label hidden in `title`. The prototype labels every one of
 * them, so those two are relabelled rather than replaced: they keep their `data-action`,
 * their handler and their permission behaviour, and only lose `.no-text`. Export and Print
 * are genuinely new (see GridExport.ts for why they are implemented rather than stubbed)
 * and are built with the same markup shape Serenity uses, so one rule set styles all four.
 *
 * ⚠ Order is set here, explicitly. The buttons arrive in whatever order Serenity created
 * them (New first, then the icons), and `.m-list-actions` is a plain flex row, so without
 * this the primary action sat on the left of its own secondaries.
 */
function buildHeaderActions(actions: HTMLElement, grid: DataGrid<any>) {
    const label = (action: string, text: string) => {
        const btn = actions.querySelector<HTMLElement>(`.tool-button[data-action="${action}"]`);
        if (!btn) return null;
        btn.classList.remove("no-text");
        const inner = btn.querySelector(".button-inner") || btn;
        // The icon is the only child worth keeping; Serenity leaves a stray space beside it.
        Array.from(inner.childNodes).forEach(n => { if (n.nodeType === Node.TEXT_NODE) n.remove(); });
        inner.appendChild(document.createTextNode(text));
        return btn;
    };

    const columns = label("column-picker", "Columns");
    const refresh = label("refresh", "Refresh");

    const make = (action: string, text: string, icon: string, onClick: () => void) => {
        const btn = el("div", `tool-button icon-tool-button m-tool-${action}`);
        btn.setAttribute("data-action", action);
        btn.setAttribute("role", "button");
        btn.setAttribute("tabindex", "0");
        const inner = el("span", "button-inner");
        inner.appendChild(el("i", `fa ${icon}`));
        inner.appendChild(document.createTextNode(text));
        btn.appendChild(inner);
        const run = () => {
            try { onClick(); }
            catch (err) { console.error(`[BWDesignSystem] ${action} failed`, err); }
        };
        btn.addEventListener("click", run);
        // Serenity's own tool buttons are divs too, so keyboard support has to be added
        // rather than inherited from a <button>.
        btn.addEventListener("keydown", e => {
            if (e.key === "Enter" || e.key === " ") { e.preventDefault(); run(); }
        });
        return btn;
    };

    const exportBtn = make("export", "Export", "fa-download", () => {
        const rows = exportCsv(grid);
        notifySuccess(`${rows} ${rows === 1 ? "row" : "rows"} written to CSV — visible columns only.`, "Export ready");
    });
    const printBtn = make("print", "Print", "fa-print", () => printGrid(grid));

    // Columns · Export · Print · Refresh · | · New
    const primary = actions.querySelector<HTMLElement>(".tool-button.add-button");
    [columns, exportBtn, printBtn, refresh].forEach(b => b && actions.appendChild(b));
    if (primary) {
        actions.appendChild(el("span", "m-list-actions-sep"));
        actions.appendChild(primary);
    }
}

/** The Compact / Comfortable segmented control the v2 doc draws at the right of the tools row. */
function buildDensity(grid: DataGrid<any>) {
    const wrap = el("div", "m-density");
    const current = readDensity();

    (["compact", "comfortable"] as Density[]).forEach(mode => {
        const b = el("button", "m-density-btn", mode === "compact" ? "Compact" : "Comfortable") as HTMLButtonElement;
        b.type = "button";
        b.classList.toggle("is-on", mode === current);
        b.addEventListener("click", () => {
            wrap.querySelectorAll(".m-density-btn").forEach(x => x.classList.remove("is-on"));
            b.classList.add("is-on");
            writeDensity(mode);
            applyDensity(grid, mode);
        });
        wrap.appendChild(b);
    });

    return wrap;
}

/**
 * ⚠ Row height cannot be changed from CSS. SleekGrid writes the height it is using into
 * --sg-row-height inline on the container AND reuses that number to compute scroll
 * offsets and which rows are in the virtual viewport, so a stylesheet rule is both
 * outranked and, if it did win, would move the paint without moving the maths — rows
 * would overlap.
 *
 * ⚠ setOptions({ rowHeight }) ALONE IS NOT ENOUGH, and this is the part that looks like
 * it works. It updates the option — getOptions().rowHeight really does read back as the
 * new value — but the CSS custom properties are written once by SleekGrid's private
 * createCssRules(), and setOptions never re-runs it. So the option and the rendering
 * disagree and every row stays at its original height. invalidateColumns() is the public
 * method that tears the rules down and rebuilds them (removeCssRules + createCssRules +
 * resizeCanvas + updateCanvasWidth), which is why the change is made in two calls with
 * render suppressed in between.
 */
function applyDensity(grid: DataGrid<any>, mode: Density) {
    const slick = grid.slickGrid as any;
    if (!slick?.setOptions) return;

    const height = rowHeights()[mode];
    if (slick.getOptions?.()?.rowHeight === height) return;

    slick.setOptions({ rowHeight: height }, true);
    slick.invalidateColumns?.();
}

/**
 * Reads the quick filter editors and reflects them as the doc's removable chips plus the
 * count on the Filters button — "Filters 2", "Type: Unit Franchise ✕", "State: Tamil Nadu ✕".
 *
 * Serenity has no concept of "which quick filters are currently set", so this asks each
 * editor directly through EditorUtils rather than tracking state of its own. That way the
 * chips cannot drift from what the grid is actually filtering by: they are derived, not
 * mirrored.
 */
/**
 * Finds the editor widget inside one quick filter item.
 *
 * ⚠ Quick filter editors do NOT carry Serenity's `.editor` class — that is the property
 * grid's convention. Here they carry their own type class (`s-StringEditor`,
 * `s-LookupEditor`), so selecting `.editor` silently matches nothing and every chip
 * quietly fails to appear. Nor can the first input be taken blindly: select2 injects its
 * own `input.select2-input` ahead of the real one. Asking which node actually has a
 * widget attached is the only reliable test, so that is what this does.
 */
export function findEditor(item: HTMLElement): { node: HTMLElement; widget: Widget<any> } | null {
    const groups = ["input, select, textarea", "[class*='Editor']"];
    for (const selector of groups) {
        for (const node of Array.from(item.querySelectorAll<HTMLElement>(selector))) {
            const widget = tryGetWidget<Widget<any>>(node);
            if (widget) return { node, widget };
        }
    }
    return null;
}

/**
 * Clears every quick filter on a card. Shared with GridStates.ts, whose empty state
 * offers the same "Clear filters" action the 07 handoff draws — both must clear through
 * the real editors so the grid and the chips cannot disagree about what is filtered.
 */
export function clearAllQuickFilters(card: HTMLElement) {
    card.querySelectorAll<HTMLElement>(".quick-filter-item").forEach(item => {
        const f = findEditor(item);
        if (!f) return;
        try {
            EditorUtils.setValue(f.widget, null);
            f.node.dispatchEvent(new Event("change", { bubbles: true }));
        } catch { /* leave this one; the others still clear */ }
    });
}

/** How many quick filters currently hold a value, and their "Label = value" summaries. */
export function activeQuickFilters(card: HTMLElement): string[] {
    const out: string[] = [];
    card.querySelectorAll<HTMLElement>(".quick-filter-item").forEach(item => {
        const found = findEditor(item);
        if (!found) return;
        let value: any, text = "";
        try {
            value = EditorUtils.getValue(found.widget);
            text = EditorUtils.getDisplayText(found.widget) || "";
        } catch { return; }
        const isEmpty = value == null || value === "" || (Array.isArray(value) && value.length === 0);
        if (isEmpty || !text) return;
        const label = (item.querySelector(".quick-filter-label")?.textContent || "").replace(/[:：]\s*$/, "").trim();
        out.push(label ? `${label} = ${text}` : text);
    });
    return out;
}

function syncFilters(card: HTMLElement, chips: HTMLElement, btn: HTMLButtonElement | null, grid: DataGrid<any>) {
    chips.textContent = "";
    let active = 0;

    card.querySelectorAll<HTMLElement>(".quick-filter-item").forEach(item => {
        const found = findEditor(item);
        if (!found) return;
        const { node: editorNode, widget } = found;

        let value: any, text = "";
        try {
            value = EditorUtils.getValue(widget);
            text = EditorUtils.getDisplayText(widget) || "";
        } catch { return; }

        const isEmpty = value == null || value === "" ||
            (Array.isArray(value) && value.length === 0);
        if (isEmpty || !text) return;

        active++;
        const label = (item.querySelector(".quick-filter-label")?.textContent || "").replace(/[:：]\s*$/, "").trim();

        const chip = el("span", "m-chip");
        if (label) chip.appendChild(el("span", "m-chip-key", label));
        chip.appendChild(el("span", "m-chip-val", text));

        const x = el("button", "m-chip-x", "✕") as HTMLButtonElement;
        x.type = "button";
        x.setAttribute("aria-label", `Remove filter ${label || text}`);
        x.addEventListener("click", () => {
            try {
                EditorUtils.setValue(widget, null);
                // setValue does not always notify for every editor type, and the quick
                // filter bar listens for a DOM change; dispatching one keeps every
                // editor kind behaving the same.
                editorNode.dispatchEvent(new Event("change", { bubbles: true }));
            } catch { /* editor refused the clear; leave the chip for the user to handle */ }
            setTimeout(() => syncFilters(card, chips, btn, grid), 0);
        });
        chip.appendChild(x);
        chips.appendChild(chip);
    });

    // Once more than one filter is on, removing them one chip at a time is tedious, and
    // the panel gives no single way back to "everything". Only shown when it has
    // something to do.
    if (active > 1) {
        const clearAll = el("button", "m-chip-clear", "Clear all") as HTMLButtonElement;
        clearAll.type = "button";
        clearAll.addEventListener("click", () => {
            card.querySelectorAll<HTMLElement>(".quick-filter-item").forEach(item => {
                const f = findEditor(item);
                if (!f) return;
                try {
                    EditorUtils.setValue(f.widget, null);
                    f.node.dispatchEvent(new Event("change", { bubbles: true }));
                } catch { /* leave this one; the others still clear */ }
            });
            setTimeout(() => syncFilters(card, chips, btn, grid), 0);
        });
        chips.appendChild(clearAll);
    }

    if (btn) {
        const badge = btn.querySelector(".m-filter-count");
        if (badge) {
            badge.textContent = active ? String(active) : "";
            (badge as HTMLElement).hidden = !active;
        }
        btn.classList.toggle("has-filters", active > 0);
    }
}
